import dayjs from "dayjs";
import { CANCEL_PAYMENT_TYPE_DISPLAY } from "./constants";
import { formatCurrency } from "../../../constants/utils";
import { SORT_DIRECTIONS } from "../tableConstants";

/**
 * Formats a single cancellation window into a readable string.
 *
 * Uses the window's `windowIndex` to determine the format:
 * - windowIndex 0: "{endValue} {endUnit} before Check-in time"
 * - windowIndex > 0: "{endValue} {endUnit} before Check-in time to {startValue} {startUnit} before Check-in time"
 */
export const formatCancellationWindow = (window) => {
  if (!window) return "";
  const { startValue, endValue, startUnit, endUnit, windowIndex } = window;
  if (windowIndex === 0) {
    return `${endValue} ${endUnit} before Check-in time`;
  }
  return `${endValue} ${endUnit} before Check-in time to ${startValue} ${startUnit} before Check-in time`;
};

/**
 * Formats all active cancellation windows for a policy into a display string.
 * Multiple windows are comma-separated.
 */
export const formatCancellationWindows = (windows) => {
  if (!windows || windows.length === 0) return "-";
  const activeWindows = windows.filter((w) => w.isActive);
  if (activeWindows.length === 0) return "-";
  return activeWindows.map(formatCancellationWindow).join(", ");
};

/**
 * Formats the cancellation payment amount based on its type.
 * PERCENT → "100% of Total Charge", FLAT → "$50.00 Flat Fee",
 * NIGHT → "1 Night(s) Charge", NO_DEDUCT → "No Deduction"
 *
 * Flat fees go through the shared formatCurrency (the same path as the
 * promotions table's flat discounts), falling back to USD like it does,
 * so both surfaces render currency identically.
 */
export const formatCancelPaymentAmount = (window, currency) => {
  if (!window) return "";
  const { cancelPaymentType, cancelPaymentAmount } = window;
  const display = CANCEL_PAYMENT_TYPE_DISPLAY[cancelPaymentType];
  if (!display) return `${cancelPaymentAmount}`;
  if (cancelPaymentType === "FLAT") {
    return display(formatCurrency(cancelPaymentAmount, currency || "USD"));
  }
  return display(cancelPaymentAmount);
};

/**
 * Formats all active cancellation window amounts for a policy.
 * Multiple windows are comma-separated.
 */
export const formatCancellationAmounts = (windows, currency) => {
  if (!windows || windows.length === 0) return "-";
  const activeWindows = windows.filter((w) => w.isActive);
  if (activeWindows.length === 0) return "-";
  return activeWindows
    .map((w) => formatCancelPaymentAmount(w, currency))
    .join(", ");
};

/**
 * Formats an ISO date string (YYYY-MM-DD) into "MMM DD, YYYY" display format.
 * Example: "2025-03-15" → "Mar 15, 2025"
 * Returns "-" for null/undefined/empty input or invalid dates.
 */
export const formatPolicyDate = (dateString) => {
  if (!dateString) return "-";
  const parsed = dayjs(dateString);
  if (!parsed.isValid()) return "-";
  return parsed.format("MMM DD, YYYY");
};

/**
 * Formats one date range for the Date Range column.
 * Example: { startDate: "2025-03-01", endDate: "2025-05-31" }
 *          → "Mar 01, 2025 - May 31, 2025"
 *
 * An open-ended range still renders: a missing side falls back to "-" via
 * formatPolicyDate rather than dropping the range from the list, so a policy is
 * never silently short a range — the whole point of this column.
 */
export const formatPolicyDateRange = (range) => {
  if (!range) return "";
  return `${formatPolicyDate(range.startDate)} - ${formatPolicyDate(range.endDate)}`;
};

/**
 * Every date range a policy applies to, oldest first.
 *
 * Takes the row's `seasonalPolicies` list — one entry per range. A lone object
 * is still accepted so a response that has not been migrated yet renders its
 * single range instead of an empty cell.
 *
 * Sorted by start date because the first range is shown inline and the rest sit
 * behind the +N badge — "first" has to mean earliest, not whatever order the
 * API happened to serialise.
 */
export const getPolicyDateRanges = (seasonalPolicies) => {
  if (!seasonalPolicies) return [];

  const ranges = Array.isArray(seasonalPolicies)
    ? seasonalPolicies
    : [seasonalPolicies];

  return ranges
    .filter((range) => range && (range.startDate || range.endDate))
    .slice()
    .sort((a, b) =>
      String(a.startDate ?? "").localeCompare(String(b.startDate ?? ""))
    );
};

/**
 * The Date Range column's display strings — one per range, first inline and the
 * remainder behind the +N badge.
 */
export const formatPolicyDateRanges = (seasonalPolicies) =>
  getPolicyDateRanges(seasonalPolicies).map(formatPolicyDateRange);

/**
 * Rate plans a policy is attached to, across all of its date ranges.
 *
 * `attachedRateplans` now hangs off each range rather than the policy, so a
 * plan used by only the winter range would vanish if this read the first range
 * alone. Union, de-duplicated, first-seen order preserved.
 */
export const getPolicyRatePlans = (seasonalPolicies) => {
  const plans = getPolicyDateRanges(seasonalPolicies).flatMap(
    (range) => range.attachedRateplans ?? []
  );
  return [...new Set(plans.filter(Boolean))];
};

/**
 * Filters policies by search query (matches against policyName and crsCode).
 */
export const filterPoliciesBySearch = (policies, query) => {
  if (!query || !query.trim()) return policies;
  const lowerQuery = query.toLowerCase().trim();
  return policies.filter(
    (policy) =>
      policy.policyName?.toLowerCase().includes(lowerQuery) ||
      policy.crsCode?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Sorts policies by isActive status.
 * "desc" = Active first, "asc" = Inactive first, null = no-op.
 * Returns a new array (no mutation).
 */
export const sortPoliciesByStatus = (policies, sortDirection) => {
  if (!sortDirection) return policies;
  return [...policies].sort((a, b) => {
    const aActive = a.isActive ? 1 : 0;
    const bActive = b.isActive ? 1 : 0;
    return sortDirection === SORT_DIRECTIONS.DESC
      ? bActive - aActive
      : aActive - bActive;
  });
};
